import { useUser } from "@clerk/react-router";
import { useEffect, useRef } from "react";

declare global {
  interface Window {
    $crisp?: unknown[][];
    CRISP_WEBSITE_ID?: string;
  }
}

type CrispChatProps = {
  enabled: boolean;
  user?: {
    email?: string | null;
    name?: string | null;
  } | null;
};

const CRISP_CHAT_HIDDEN_EMAILS = new Set([
  "michal.jackowski@anylawyer.com",
  "michal.jackowski@dsk-kancelaria.pl",
]);

export function shouldHideCrispChatForEmail(email?: string | null) {
  if (!email) {
    return false;
  }

  return CRISP_CHAT_HIDDEN_EMAILS.has(email.trim().toLowerCase());
}

export function CrispChat({ enabled, user }: CrispChatProps) {
  const scriptLoadedRef = useRef(false);
  const userEmailRef = useRef<string | null>(null);
  const chatEnabled = enabled && !shouldHideCrispChatForEmail(user?.email);

  useEffect(() => {
    if (
      !chatEnabled ||
      scriptLoadedRef.current ||
      typeof window === "undefined"
    ) {
      return;
    }

    const websiteId = import.meta.env.VITE_CRISP_WEBSITE_ID;
    if (!websiteId) {
      console.warn("VITE_CRISP_WEBSITE_ID is not set");
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://client.crisp.chat/l.js"]'
    );

    if (!existingScript) {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = websiteId;

      const script = document.createElement("script");
      script.src = "https://client.crisp.chat/l.js";
      script.async = true;
      document.head.appendChild(script);
    }

    scriptLoadedRef.current = true;
  }, [chatEnabled]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.$crisp) {
      return;
    }

    window.$crisp.push(["do", chatEnabled ? "chat:show" : "chat:hide"]);
  }, [chatEnabled]);

  useEffect(() => {
    if (
      !chatEnabled ||
      !user ||
      typeof window === "undefined" ||
      !window.$crisp
    ) {
      return;
    }

    const email = user.email ?? null;
    if (userEmailRef.current === email) {
      return;
    }

    const name = user.name ?? null;
    if (name) {
      window.$crisp.push(["set", "user:nickname", [name]]);
    }

    if (email) {
      window.$crisp.push(["set", "user:email", [email]]);
    }

    userEmailRef.current = email;
  }, [chatEnabled, user]);

  return null;
}

type AuthenticatedCrispChatProps = {
  enabled: boolean;
};

export function AuthenticatedCrispChat({
  enabled,
}: AuthenticatedCrispChatProps) {
  const { user, isLoaded } = useUser();

  return (
    <CrispChat
      enabled={enabled}
      user={
        isLoaded && user
          ? {
              email: user.primaryEmailAddress?.emailAddress,
              name: user.fullName || user.firstName,
            }
          : null
      }
    />
  );
}
